import React from "react";
import config from "../../config";
import Grid from "@material-ui/core/Grid";
import { translate } from "../../i18n/customI18nProvider";
import { Button } from "@mui/material";

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, MouseSensor, TouchSensor } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DepositForm from "./DepositForm";
import { Add } from "@mui/icons-material";
import { getBarPossibleDeposits, reoderBarConsumptions, reorderBarPossibleDeposits } from "../../services/bar.service";
import { notifyError, notifySuccess } from "../../util";
import { useNotify } from "ra-core";
      
const styles = {
    item: {
        container: {
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            border: "1px solid lightgray",
            borderRadius: "5px",
            padding: "10px",
            paddingTop: "20px",
            paddingBottom: "20px",
            userSelect: "none",
            WebkitUserSelect: "none",
            touchAction: "pan-y"
        },
        button: {
            textAlign: "center",
            alignContent: "center",
            background: "lightGray",
            borderRadius: "5px",
            height: "30px",
            width: "30px"
        }
    }
}

const DepositList = ({ deposits, currencyCode, bar_id }) => {
    const [list, setList] = React.useState([]);
    const [form, setForm] = React.useState({show: false, element: null});

    const notify = useNotify();
    
    
    const mouseSensor = useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    });
    const touchSensor = useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    });
    
    const sensors = useSensors(
      mouseSensor,
      touchSensor
    );
    
    const handleFormClose = () => {
        setForm({ show: false, element: null });
        fetchDeposits();
    }
    
    const handleDragStart = (event) => {
        if (window.navigator && window.navigator.vibrate) {
          window.navigator.vibrate(25);
        }
    };
    
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            const newList = arrayMove(
                list,
                list.findIndex((i) => i === active.id),
                list.findIndex((i) => i === over.id)
            );
            setList(newList);
            const response = await reorderBarPossibleDeposits(bar_id, newList);
            if (response.error) {
                notifyError(response.error);
            } else {
                notifySuccess("custom.order_saved");
            }
        }
    };

    React.useEffect(() => {
        fetchDeposits();
    }, []);
    
    const fetchDeposits = async () => {
        try {
            const deposits = await getBarPossibleDeposits(bar_id);
            if (deposits && deposits.length >= 0) {
                deposits.sort((a, b) => a.index - b.index);
                setList(deposits);
            }
        } catch (error) {
            console.error("Error fetching deposits:", error);
        }
    };
    
    const findDeposit = (id) => {
        let deposit = null;
        for (const element of deposits) {
            if (element._id === id) {
                deposit = element;
                break;
            }
        }
        return deposit;
    }
    
    const findColor = (colorId) => {
        const colorObj = config.consumption_colors.find((color) => color.id === colorId);
        return colorObj ? colorObj.colorCode : config.baseColorLight;
    };
    
    const formatPrice = (price) => {
        return `${currencyCode} ${price.toFixed(2)}`;
    }
    
    const SortableDepositItem = ({ deposit, formatPrice, findColor, baseColor }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: deposit._id });
    
        const style = {
            display: "flex",
            position: "relative",
            transform: CSS.Transform.toString(transform),
            transition,
            cursor: isDragging ? "grabbing" : "grab",
            opacity: isDragging ? 0.5 : 1,
            zIndex: isDragging ? 100 : 1,
            marginBottom: "10px",
        };
        return (
            <div style={style} ref={setNodeRef} {...attributes} {...listeners}>
                <div onClick={() => { setForm({ show: true, element: deposit }) }} style={{ ...styles.item.container, background: findColor(deposit.color) }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                            <h4 style={{ fontSize: "15px", color: config.baseColor, margin: "0px" }}>{deposit.name}</h4>
                        </div>
                        <div style={{ display: "flex", alignItems: "center"}}>
                            <p style={{ margin: "0px", fontSize: "11px" }}>{formatPrice(deposit.price)}</p>
                        </div>
                    </div>
                    <div 
                        style={{ display: "flex", background: "white", padding: "5px", borderRadius: "5px", height: "30px", marginTop: "auto", marginBottom: "auto" }}
                    >
                        <div style={styles.item.button}>-</div>
                        <p style={{ margin: "0px", width: "30px", textAlign: "center", alignSelf: "center" }}>0</p>
                        <div style={styles.item.button}>+</div>
                    </div>
                </div>
            </div>
        )
    };
    
    return (
        <div>
            <div style={{ width: "100%" }}>
                <h3 style={{ width: "100%", marginBottom: "0px", borderBottom: "2px solid" + config.baseColor, color: config.baseColor }}>
                    {translate("custom.possible_deposits")}
                </h3>
                <p style={{ fontSize: "14px", color: "gray", fontStyle: "italic", marginBottom: "20px", marginTop: "5px" }}> {translate('custom.possible_deposit_list_info')}</p>
            </div>
            <div style={{ maxWidth: "800px", margin: "auto" }}>
                <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                    onDragStart={handleDragStart}
                >
                    <SortableContext 
                        items={list} 
                        strategy={verticalListSortingStrategy}
                    >
                        {list.map((deposit) => (
                            <Grid item xs={12} key={deposit}>
                                <SortableDepositItem 
                                    deposit={findDeposit(deposit)}
                                    formatPrice={formatPrice}
                                    findColor={findColor}
                                />
                            </Grid>
                        ))}
                    </SortableContext>
                </DndContext>
                {
                    (list.length < deposits.length) &&
                    <div style={{ ...styles.item.container, justifyContent: "center", alignItems: "center", background: "white", cursor: "pointer" }} onClick={() => { setForm({ show: true, element: null }) }}>
                        <Button startIcon={<Add />}>
                            {translate('custom.add')}
                        </Button>
                    </div>  
                }
            </div>
            <DepositForm barId={bar_id} currencyCode={currencyCode} show={form.show} deposit={form.element} deposits={ deposits } onClose={ handleFormClose } selectedDeposits={list} />
        </div>
    );
};

export default DepositList;
